import { Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '@client/hooks/useAuth';
import { useTenantQueries } from '@client/queries/useTenantQueries';
import { type IGrantCreditsPayload } from '@client/services/credits.service';
import { ERoutePaths } from '@client/types/router.type';
import { logger } from '@client/utils/logger.util';
import { ELedgerKind } from '@common/types';

type TGrantCreditsModalProps = {
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onFinish: (
    values: Omit<IGrantCreditsPayload, 'idempotencyKey'>,
  ) => Promise<void>;
};

export const GrantCreditsModal: React.FC<TGrantCreditsModalProps> = ({
  open,
  loading,
  onCancel,
  onFinish,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form] = Form.useForm<Omit<IGrantCreditsPayload, 'idempotencyKey'>>();

  const { useAllTenants } = useTenantQueries();
  const { data: tenants, isLoading: tenantsLoading } = useAllTenants();

  const handleOk = async (): Promise<void> => {
    await form
      .validateFields()
      .then(async (values) => {
        await onFinish(values);
        form.resetFields();
      })
      .catch((error) => {
        logger.warn(error);
      });
  };

  const handleCancel = (): void => {
    form.resetFields();
    onCancel();
  };

  const noTenantsContent = (
    <Typography.Text type="secondary">
      {t('form.credits.tenant.noTenants')}{' '}
      <Link to={ERoutePaths.TENANTS} onClick={handleCancel}>
        {t('form.credits.tenant.createLink')}
      </Link>
    </Typography.Text>
  );

  const kindLabels: Record<ELedgerKind, string> = {
    [ELedgerKind.GRANT]: t('form.credits.kind.grant'),
    [ELedgerKind.DEBIT]: t('form.credits.kind.debit'),
    [ELedgerKind.REFUND]: t('form.credits.kind.refund'),
    [ELedgerKind.ADJUSTMENT]: t('form.credits.kind.adjustment'),
  };

  return (
    <Modal
      title={t('form.credits.modal.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={t('form.credits.modal.ok')}
      mask={{ closable: false }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          userId: user?.uid ?? '',
          tenantId: undefined,
          amount: 1,
          kind: ELedgerKind.GRANT,
          referenceId: undefined,
        }}
      >
        <Form.Item
          label={t('form.credits.tenant')}
          name="tenantId"
          tooltip={t('form.credits.tenant.tooltip')}
          rules={[
            {
              required: true,
              message: t('form.required', { field: t('form.credits.tenant') }),
            },
          ]}
          extra={!tenantsLoading && !tenants?.length ? noTenantsContent : null}
        >
          <Select
            showSearch={{ optionFilterProp: 'label' }}
            loading={tenantsLoading}
            placeholder={t('form.credits.tenant.placeholder')}
            options={(tenants ?? []).map((tenant) => ({
              value: tenant.id as string,
              label: tenant.company
                ? `${tenant.name} (${tenant.company})`
                : tenant.name,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={t('form.credits.userId')}
          name="userId"
          tooltip={t('form.credits.userId.tooltip')}
          rules={[
            {
              required: true,
              message: t('form.required', { field: t('form.credits.userId') }),
            },
          ]}
          extra={t('form.credits.userId.extra')}
        >
          <Input placeholder={t('form.credits.userId.placeholder')} />
        </Form.Item>
        <Form.Item
          label={t('form.credits.amount')}
          name="amount"
          tooltip={t('form.credits.amount.tooltip')}
          rules={[
            {
              required: true,
              message: t('form.required', { field: t('form.credits.amount') }),
            },
          ]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label={t('form.credits.kind')}
          name="kind"
          tooltip={{
            title: (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {Object.values(ELedgerKind).map((k) => (
                  <li key={k}>{kindLabels[k] ?? k}</li>
                ))}
              </ul>
            ),
          }}
          rules={[
            {
              required: true,
              message: t('form.required', { field: t('form.credits.kind') }),
            },
          ]}
        >
          <Select
            options={Object.values(ELedgerKind).map((k) => ({
              value: k,
              label: kindLabels[k] ?? k,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={t('form.credits.referenceId')}
          name="referenceId"
          tooltip={t('form.credits.referenceId.tooltip')}
          extra={t('form.credits.referenceId.extra')}
        >
          <Input placeholder={t('form.credits.referenceId.placeholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
